import {
  AfterViewInit,
  Component,
  OnInit
} from '@angular/core';

import { Router } from '@angular/router';

import { environment as ENV } from '../../../../../environments/environment';

import { AuthService } from '../../../../services/authService/auth.service';
import { UserService } from '../../../../services/apiCalls/userService/user.service';


declare const $: any;


@Component({
  selector: 'app-s-sidebar',
  templateUrl: './s-sidebar.component.html',
  styleUrls: ['./s-sidebar.component.css']
})
export class SSidebarComponent implements OnInit, AfterViewInit {

  public reportFile: any;
  public currentUser: any;


  /*
   * These control whether the dropdowns are manually opened.
   *
   * We initialize them from the current URL so that a refresh
   * does not close the currently active menu.
   */
  public settlementOpen = false;
  public ridersOpen = false;
  public commissionOpen = false;
  public reportsOpen = false;
  public settingsOpen = false;


  constructor(
    private route: Router,
    private userService: UserService,
    private authService: AuthService
  ) {
  }


  // ============================================================
  // INITIALIZATION
  // ============================================================

  ngOnInit(): void {

    const userToken = sessionStorage.getItem(ENV.USERTOKEN);

    if (userToken) {

      try {

        this.currentUser = JSON.parse(userToken).owner;

      } catch (error) {

        console.error(
          'Unable to parse current user:',
          error
        );

        this.currentUser = {};

      }

    } else {

      this.currentUser = {};

    }


    /*
     * IMPORTANT:
     *
     * Determine the active parent from the CURRENT URL.
     *
     * This runs when the application is refreshed.
     *
     * Therefore:
     *
     * /admin/settlement-history
     *
     * will automatically open Settlement.
     *
     * /admin/settings/manage-zones
     *
     * will automatically open Settings.
     *
     * /admin/beneficiaries
     *
     * will automatically open Commission Setup.
     */
    this.setOpenMenusFromCurrentRoute();

  }


  ngAfterViewInit(): void {

    /*
     * Give Angular time to render the sidebar before
     * synchronizing with MetisMenu.
     */
    setTimeout(() => {

      this.syncMenuWithRoute();

    }, 0);

  }


  // ============================================================
  // CURRENT URL
  // ============================================================

  private getCurrentUrl(): string {

    /*
     * Remove query parameters and fragments.
     *
     * Example:
     *
     * /admin/settings/manage-zones?page=2
     *
     * becomes:
     *
     * /admin/settings/manage-zones
     */
    return this.route.url
      .split('?')[0]
      .split('#')[0];

  }


  // ============================================================
  // EXACT CHILD ROUTE
  // ============================================================

  isActiveRoute(route: string): boolean {

    return this.getCurrentUrl() === route;

  }


  // ============================================================
  // SETTLEMENT
  // ============================================================

  isSettlementActive(): boolean {

    const currentUrl = this.getCurrentUrl();

    return (
      currentUrl === '/admin/settlement' ||
      currentUrl === '/admin/settlement-history'
    );

  }


  // ============================================================
  // RIDERS
  // ============================================================

  isRidersActive(): boolean {

    const currentUrl = this.getCurrentUrl();

    return (
      currentUrl === '/admin/riders'
    );

  }


  // ============================================================
  // COMMISSION
  // ============================================================

  isCommissionActive(): boolean {

    const currentUrl = this.getCurrentUrl();

    return (
      currentUrl === '/admin/commission-sharing' ||
      currentUrl === '/admin/beneficiaries' ||
      currentUrl === '/admin/transaction-types'
    );

  }


  // ============================================================
  // REPORTS
  // ============================================================

  isReportsActive(): boolean {

    const currentUrl = this.getCurrentUrl();

    return (
      currentUrl === '/admin/report/transaction'
    );

  }


  // ============================================================
  // SETTINGS
  // ============================================================

  isSettingsActive(): boolean {

    const currentUrl = this.getCurrentUrl();

    return (
      currentUrl === '/admin/settings/payment-channel' ||
      currentUrl === '/admin/settings/manage-zones' ||
      currentUrl === '/admin/settings/configurations' ||
      currentUrl === '/admin/settings/change-password'
    );

  }


  // ============================================================
  // INITIAL OPEN STATE
  // ============================================================

  private setOpenMenusFromCurrentRoute(): void {

    /*
     * Settlement
     */
    this.settlementOpen = this.isSettlementActive();


    /*
     * Riders
     */
    this.ridersOpen = this.isRidersActive();


    /*
     * Commission
     */
    this.commissionOpen = this.isCommissionActive();


    /*
     * Reports
     */
    this.reportsOpen = this.isReportsActive();


    /*
     * Settings
     */
    this.settingsOpen = this.isSettingsActive();

  }


  // ============================================================
  // SETTLEMENT TOGGLE
  // ============================================================

  toggleSettlement(): void {

    this.settlementOpen = !this.settlementOpen;

  }


  // ============================================================
  // RIDERS TOGGLE
  // ============================================================

  toggleRiders(): void {

    this.ridersOpen = !this.ridersOpen;

  }


  // ============================================================
  // COMMISSION TOGGLE
  // ============================================================

  toggleCommission(): void {

    this.commissionOpen = !this.commissionOpen;

  }


  // ============================================================
  // REPORTS TOGGLE
  // ============================================================

  toggleReports(): void {

    this.reportsOpen = !this.reportsOpen;

  }


  // ============================================================
  // SETTINGS TOGGLE
  // ============================================================

  toggleSettings(): void {

    this.settingsOpen = !this.settingsOpen;

  }


  // ============================================================
  // AFTER NAVIGATION
  // ============================================================

  private syncMenuWithRoute(): void {

    /*
     * The URL is the source of truth.
     *
     * If the current route belongs to a parent,
     * keep that parent open.
     */

    if (this.isSettlementActive()) {

      this.settlementOpen = true;

    }

    if (this.isRidersActive()) {

      this.ridersOpen = true;

    }

    if (this.isCommissionActive()) {

      this.commissionOpen = true;

    }

    if (this.isReportsActive()) {

      this.reportsOpen = true;

    }

    if (this.isSettingsActive()) {

      this.settingsOpen = true;

    }


    /*
     * Sync MetisMenu's visual state.
     *
     * Your existing sidebar CSS uses `in` for the
     * opened dropdown, so we add/remove `in` here.
     */
    setTimeout(() => {

      this.applyDropdownState(
        '#settlement',
        this.settlementOpen
      );

      this.applyDropdownState(
        '#riders',
        this.ridersOpen
      );

      this.applyDropdownState(
        '#commission',
        this.commissionOpen
      );

      this.applyDropdownState(
        '#transaction',
        this.reportsOpen
      );

      this.applyDropdownState(
        '#settings',
        this.settingsOpen
      );

    }, 0);

  }


  // ============================================================
  // APPLY DROPDOWN STATE
  // ============================================================

  private applyDropdownState(
    parentSelector: string,
    open: boolean
  ): void {

    const parent = $(parentSelector);

    const childMenu = parent.children(
      '.childNavDropDown'
    );

    const parentLink = parent.children(
      'a.has-arrow'
    );


    if (open) {

      /*
       * Open dropdown
       */
      childMenu.addClass('in');
      childMenu.addClass('show');

      /*
       * Tell the menu it is expanded.
       */
      parentLink.attr(
        'aria-expanded',
        'true'
      );

    } else {

      /*
       * Close dropdown
       */
      childMenu.removeClass('in');
      childMenu.removeClass('show');

      /*
       * Tell the menu it is collapsed.
       */
      parentLink.attr(
        'aria-expanded',
        'false'
      );

    }

  }


  // ============================================================
  // LOGOUT
  // ============================================================

  public logOut() {
    this.userService.logOut().subscribe(() => {
      this.authService.logOut();
    });
    this.authService.logOut();
    this.route.navigate(['/']);
  }


}
